import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { useLocalStorage, useShallowCompareEffect } from 'react-use'
import { v4 as uuid } from 'uuid'

import questions from './questions'

import reducer from './reducer'
import { getUser, updateAnswers } from './actions'

const QuizContext = createContext()

const { Provider } = QuizContext

const initialState = {
  currentScreen: process.env.NODE_ENV === 'development' ? 'results' : 'intro',
  selectedQuestion: 0,
  navDirection: 1,
  questions,
  answers: null,
  visitedQuestions: [],
  userDataLoaded: false,
  showWelcome: true,
}

const themes = questions.reduce((t, q, id) => {
  q.id = id
  const { theme } = q

  const themeIndex = t.findIndex(el => el.theme === theme)
  if (themeIndex > -1) {
    t[themeIndex].questions.push(q)
  } else {
    t.push({ theme, questions: [q] })
  }
  return t
}, [])

const numQuestions = questions.length

const QuizProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [userId, setUserId, removeUserId] = useLocalStorage('broadridge-quiz')
  const {
    selectedQuestion,
    currentScreen,
    visitedQuestions,
    answers,
    questions,
    userDataLoaded,
  } = state

  const currentQuestion = questions[selectedQuestion]
  const currentAnswer = answers ? answers[selectedQuestion] : null
  const isVisited = visitedQuestions ? visitedQuestions[selectedQuestion] : null
  const isLast = selectedQuestion >= numQuestions - 1

  useEffect(() => {
    if (currentScreen !== 'intro' && !userDataLoaded) {
      if (userId === undefined) {
        setUserId(uuid())
      } else {
        getUser(dispatch, userId)
      }
    }
  }, [userId, setUserId, currentScreen, userDataLoaded, dispatch])

  useShallowCompareEffect(() => {
    updateAnswers(userId, answers)
  }, [visitedQuestions])

  const reset = () => {
    setUserId(removeUserId)
    dispatch({ type: 'RESET', screen: 'intro' })
  }

  return (
    <Provider
      value={{
        ...state,
        themes,
        currentQuestion,
        currentAnswer,
        isVisited,
        isLast,
        numQuestions,
        dispatch,
        reset,
      }}
    >
      {process.env.NODE_ENV === 'development' ? answers && children : children}
    </Provider>
  )
}

export default QuizProvider

export const useQuizContext = () => useContext(QuizContext)
